/**
 * AI assistant UI: right-side chat drawer SHELL. No floating launcher — the
 * drawer opens via the `openAiAssistant` window event (Setup-guide row,
 * preventDefault handshake in the provider). Rendered exclusively by
 * AiAssistantProvider (which also gates on ai:use), so it can always read
 * the assistant context. Portaled to <body> (first-render createPortal — see
 * the note at the return) to detach it from the page layout flow.
 *
 * P5-S2: the transcript + composer moved to AiChatPanel (shared with the AI
 * studio); this file owns only positioning, header, backdrop and Escape.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslations } from "next-intl";
import { IconBug, IconHistory, IconMessagePlus, IconSparkles, IconX } from "@tabler/icons-react";
import { HoverInfo } from "@/components/HoverInfo/HoverInfo";
import { isCreateEventRoute } from "@/lib/immersiveRoutes";
import { IconButton } from "@/components/Buttons/IconButton";
import { useAiAssistant } from "@/providers/AiAssistantProvider";
import { AiChatPanel } from "./AiChatPanel";
import { ConversationHistoryPanel } from "./chat/ConversationHistoryPanel";
import { BugReportModal } from "./BugReportModal";
import { hasQuotaRejection, useAiCreditsStatus } from "@/hooks/useAiCreditsStatus";
import { AiCreditsRing } from "./AiCreditsRing";

export function AiAssistantDrawer() {
  const t = useTranslations("aiAssistant");
  const { open, scopeKind, closeDrawer, newConversation, messages } = useAiAssistant();
  // The header ring must agree with the transcript: once the server has
  // refused a turn, a ring still reading "credits left" is the contradiction
  // users reported (the spend guard is all-or-nothing, so a surface that bills
  // several credits per turn is refused while a couple still show as left).
  const credits = useAiCreditsStatus({ rejected: hasQuotaRejection(messages) });
  const reduceMotion = useReducedMotion();
  const [bugOpen, setBugOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const isImmersiveRoute = isCreateEventRoute(useLocation().pathname);

  // Close the history overlay whenever the drawer closes, so reopening lands
  // on the chat (not a stale history view).
  useEffect(() => {
    if (!open) setHistoryOpen(false);
  }, [open]);

  // An open drawer must not overlay the create-event wizard — close it
  // when the user navigates there (it would cover the wizard, full-screen
  // on mobile).
  useEffect(() => {
    if (isImmersiveRoute && open) closeDrawer();
  }, [isImmersiveRoute, open, closeDrawer]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDrawer();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, closeDrawer]);

  // Docked rail: while open, publish the rail width as a global CSS var so the
  // layout shells can inset their content (sm:pr-[var(--ai-rail-push)]) — the
  // app shifts left and stays usable instead of being covered.
  // Keep the 340px in sync with the rail's sm:w-[340px] below.
  // The SAME var must offset every fixed full-viewport surface below z-10000:
  // bottom-right floaters (right-[calc(…+var(--ai-rail-push))]), centered
  // modal overlays (sm:pr-[var(--ai-rail-push)], see ModalWrapper) and
  // right-anchored panels (sm:right-[var(--ai-rail-push)], slotting) — a new
  // overlay without it renders under the rail.
  //
  // We publish --ai-rail-WIDTH; index.css derives --ai-rail-push from it behind
  // a min-width:640px media query. The indirection is load-bearing, not
  // cosmetic: this writes an INLINE property on documentElement, and an inline
  // declaration beats every stylesheet rule — so a media-queried `:root` could
  // never gate the same name. Below sm the rail is a full-screen overlay with
  // its own backdrop and must push nothing; consumers that carry a Tailwind
  // `sm:` prefix were already safe, but inline style={} offsets (which have no
  // media query at all) would otherwise inset a phone-width dialog by 340px.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--ai-rail-width", open ? "340px" : "0px");
    return () => root.style.setProperty("--ai-rail-width", "0px");
  }, [open]);

  // Direct createPortal, NOT <PortalToBody> (which returns null until a
  // post-paint effect): on a dashboard↔event shell swap the old drawer
  // unmounts and this one mounts in the SAME router commit — portaling on
  // the first render keeps the drawer DOM continuous across that commit
  // instead of blanking for a painted frame. Client-only SPA, so
  // document.body is always available during render (POnboarding does the
  // same).
  return createPortal(
    <>
      {/* initial={false}: when the drawer remounts ALREADY open (dashboard↔
          event shell swap — the open flag persists in the ai-store), it must
          appear in place, not replay the slide-in. A fresh user open flips
          `open` after the first render, so that entrance still animates. */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="ai-backdrop"
            aria-hidden
            className="fixed inset-0 z-10000 no-print bg-black/40 sm:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={closeDrawer}
          />
        )}
        {open && (
          <motion.div
            key="ai-drawer"
            data-testid="ai-assistant-drawer"
            className="fixed top-0 right-0 z-10000 no-print h-dvh w-full surface-primary outline-quaternary flex flex-col min-h-0 sm:w-[340px] sm:border-l"
            initial={reduceMotion ? { opacity: 0 } : { x: "100%" }}
            animate={reduceMotion ? { opacity: 1 } : { x: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { x: "100%" }}
            // Match the help-center rail: a 250ms transform tween on the
            // standard-decelerate curve, not a spring (docs ChatPanel.tsx).
            transition={
              reduceMotion
                ? { duration: 0.1 }
                : { type: "tween", duration: 0.25, ease: [0.4, 0, 0.2, 1] }
            }
          >
            {/* Header — pinned to h-12 (48px) so its bottom border lines up
                with the app bar's. SAppBar's real height is py-2 (16px) around
                its tallest child (the OnboardingPill, ~32px) = 48px — NOT the
                h-14 loading skeleton. Both start at top-0, so equal height =
                one continuous line across the screen. */}
            <div className="shrink-0 flex items-center gap-2 px-3.5 h-12 border-b outline-quaternary">
              {/* Leading slot = the credits gauge (fills with the share USED,
                  hover reveals the exact remaining balance; blue → warning →
                  error). The brand sparkle only bridges the load gap so the
                  header never jumps. */}
              {credits.loaded ? (
                <AiCreditsRing credits={credits} />
              ) : (
                <IconSparkles size={18} className="shrink-0 font-blue" />
              )}
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <span className="font-medium text-sm truncate">{t("title")}</span>
                <span className="shrink-0 rounded-md surface-secondary px-1.5 py-0.5 text-[11px] font-tertiary">
                  {scopeKind === "company" ? t("scopeCompany") : t("scopeEvent")}
                </span>
              </div>
              <HoverInfo message={t("support.menuReportBug")}>
                <IconButton
                  iconOnly
                  ariaLabel={t("support.menuReportBug")}
                  icon={IconBug}
                  text=""
                  iconSize={18}
                  padding="p-1.5"
                  color="font-primary"
                  onClickAction={() => setBugOpen(true)}
                />
              </HoverInfo>
              <HoverInfo message={t("history")}>
                <IconButton
                  iconOnly
                  ariaLabel={t("history")}
                  icon={IconHistory}
                  text=""
                  iconSize={18}
                  padding="p-1.5"
                  color={historyOpen ? "font-blue" : "font-primary"}
                  onClickAction={() => setHistoryOpen((v) => !v)}
                />
              </HoverInfo>
              <HoverInfo message={t("newConversation")}>
                <IconButton
                  iconOnly
                  ariaLabel={t("newConversation")}
                  icon={IconMessagePlus}
                  text=""
                  iconSize={18}
                  padding="p-1.5"
                  color="font-primary"
                  onClickAction={() => {
                    setHistoryOpen(false);
                    newConversation();
                  }}
                />
              </HoverInfo>
              <HoverInfo message={t("close")}>
                <IconButton
                  iconOnly
                  ariaLabel={t("close")}
                  icon={IconX}
                  text=""
                  iconSize={18}
                  padding="p-1.5"
                  color="font-primary"
                  onClickAction={closeDrawer}
                />
              </HoverInfo>
            </div>

            {historyOpen ? (
              <ConversationHistoryPanel onClose={() => setHistoryOpen(false)} />
            ) : (
              <AiChatPanel />
            )}
          </motion.div>
        )}
      </AnimatePresence>
      {bugOpen && <BugReportModal onClose={() => setBugOpen(false)} />}
    </>,
    document.body,
  );
}
